/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { SelectField, SelectFieldProps } from 'bento/SelectField';
import {
  checkFormHasError,
  getFormHelperText,
  handleBlurFormField,
  handleChangeFormField,
} from '@tock/shared/ui/forms/formHelpers';
import { useField } from 'formik';

/**
 * A Select menu integrated with Formik for error display.
 * Add options containing text using HTML <option> tags
 * ```
 *   <FormSelectField label="Color" name="color" id="color">
 *     <option value="red">Red</option>
 *     <option value="green">Green</option>
 *     <option value="blue">Blue</option>
 *   </FormSelectField>
 * ```
 * For options that require supplementary content (icons etc) see the custom select menu story.
 *
 * @see [Custom select story](https://tock-assets.tocktix.com/styleguide/dashboard/index.html?path=/story/components-selectfield-formselectfield--custom-select)
 * @deprecated Deprecated — use pantry Select instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-select--docs
 * Migration: Replace FormSelectField with the pantry Select component wired to your form library.
 */
export function FormSelectField({
  SelectProps,
  children,
  helperText,
  name,
  id,
  onChange,
  onBlur,
  error, // purposefully excluded from `rest`
  SelectDisplayProps,
  ...rest
}: SelectFieldProps) {
  const idValue = id ?? SelectProps?.inputProps?.id ?? name;
  const fieldProps = {
    ...SelectProps?.inputProps,
    name: name ? name : SelectProps?.inputProps?.name,
    id: idValue,
  };
  const [field, meta] = useField(fieldProps);

  const handleChange = (event: React.ChangeEvent<any>, child: any) =>
    handleChangeFormField(field, event, onChange ? onChange : (SelectProps?.onChange as any));

  const handleBlur = (event: React.FocusEvent<any>) =>
    handleBlurFormField(field, event, onBlur ? onBlur : SelectProps?.onBlur);

  const hasError = checkFormHasError(meta);

  const helperTextValue = getFormHelperText(hasError, meta, helperText);

  return (
    <SelectField
      error={hasError}
      helperText={helperTextValue as string}
      {...(field as any)}
      {...rest}
      id={idValue}
      onChange={handleChange}
      onBlur={handleBlur}
      SelectProps={SelectProps}
      SelectDisplayProps={SelectDisplayProps}
    >
      {children}
    </SelectField>
  );
}
